import * as React from "react";
import { ExportButtonProps } from "../../types/stripe";

/**
 * ExportButton Component
 * 
 * A flexible export button that supports multiple formats and provides
 * visual feedback during export operations.
 */
const ExportButton: React.FC<ExportButtonProps> = ({
  onExport,
  formats = ['csv', 'xlsx', 'pdf'],
  isExporting = false,
  disabled = false,
  size = 'md',
  variant = 'outline',
  className = "",
  id
}) => {
  const [showDropdown, setShowDropdown] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = (format: 'csv' | 'xlsx' | 'pdf') => {
    onExport(format);
    setShowDropdown(false);
  };

  const sizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg'
  };

  const variantClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline-secondary'
  };

  const formatIcons = {
    csv: 'fas fa-file-csv',
    xlsx: 'fas fa-file-excel',
    pdf: 'fas fa-file-pdf'
  };

  const formatLabels = {
    csv: 'CSV',
    xlsx: 'Excel',
    pdf: 'PDF'
  };

  const formatDescriptions = {
    csv: 'Comma-separated values file',
    xlsx: 'Microsoft Excel spreadsheet',
    pdf: 'Portable document format'
  };

  // Single format export (no dropdown)
  if (formats.length === 1) {
    const format = formats[0];
    return (
      <button
        id={id}
        type="button"
        className={`btn ${variantClasses[variant]} ${sizeClasses[size]} ${className}`.trim()}
        onClick={() => handleExport(format)}
        disabled={disabled || isExporting}
        title={`Export as ${formatLabels[format]}`}
      >
        {isExporting ? (
          <>
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Exporting...</span>
            </div>
            Exporting...
          </>
        ) : (
          <>
            <i className={`${formatIcons[format]} me-2`} aria-hidden="true"></i>
            Export {formatLabels[format]}
          </>
        )}
      </button>
    );
  }

  // Multiple formats (dropdown)
  return (
    <div className={`export-button-container ${className}`.trim()} id={id} ref={dropdownRef}>
      <div className="dropdown">
        <button
          type="button"
          className={`btn ${variantClasses[variant]} ${sizeClasses[size]} dropdown-toggle d-flex align-items-center`}
          onClick={() => !disabled && !isExporting && setShowDropdown(!showDropdown)}
          disabled={disabled || isExporting}
          aria-expanded={showDropdown}
          aria-haspopup="true"
        >
          {isExporting ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Exporting...</span>
              </div>
              Exporting...
            </>
          ) : (
            <>
              <i className="fas fa-download me-2" aria-hidden="true"></i>
              Export
            </>
          )}
        </button>

        {showDropdown && !isExporting && !disabled && (
          <div className="dropdown-menu show">
            <div className="dropdown-header">
              <i className="fas fa-download me-2" aria-hidden="true"></i>
              Choose Export Format
            </div>
            {formats.map((format) => (
              <button
                key={format}
                type="button"
                className="dropdown-item d-flex align-items-start"
                onClick={() => handleExport(format)}
              >
                <div className="me-3 mt-1">
                  <i className={formatIcons[format]} aria-hidden="true"></i>
                </div>
                <div>
                  <div className="fw-bold">{formatLabels[format]}</div>
                  <small className="text-muted">{formatDescriptions[format]}</small>
                </div>
              </button>
            ))}
            <hr className="dropdown-divider" />
            <div className="dropdown-item-text">
              <small className="text-muted">
                <i className="fas fa-info-circle me-1" aria-hidden="true"></i>
                Export includes current filter settings
              </small>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .export-button-container {
          position: relative;
          display: inline-block;
        }

        .dropdown-menu {
          min-width: 280px;
          padding: 0.5rem 0;
          border: 1px solid rgba(0, 0, 0, 0.15);
          border-radius: 0.375rem;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
          animation: fadeInScale 0.15s ease-out;
        }

        .dropdown-header {
          padding: 0.5rem 1rem;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          color: var(--bs-gray-600);
          font-weight: 600;
          border-bottom: 1px solid var(--bs-gray-200);
        }

        .dropdown-item {
          padding: 0.75rem 1rem;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
          transition: background-color 0.15s ease;
        }

        .dropdown-item:hover {
          background-color: var(--bs-gray-100);
        }

        .dropdown-item:focus {
          background-color: var(--bs-gray-100);
          outline: none;
        }

        .dropdown-item:active {
          background-color: var(--bs-gray-200);
        }

        .dropdown-item-text {
          padding: 0.5rem 1rem;
          color: var(--bs-gray-600);
        }

        .dropdown-divider {
          margin: 0.5rem 0;
        }

        /* Button states */
        .btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .btn.dropdown-toggle::after {
          margin-left: 0.5rem;
        }

        /* Loading spinner */
        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
        }

        /* Animations */
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        /* Responsive adjustments */
        @media (max-width: 576px) {
          .dropdown-menu {
            min-width: 250px;
            left: 50% !important;
            transform: translateX(-50%);
          }
          
          .btn {
            width: 100%;
          }
        }

        /* Focus and accessibility */
        .btn:focus {
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }

        .dropdown-item:focus {
          box-shadow: inset 0 0 0 2px rgba(13, 110, 253, 0.25);
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .dropdown-menu {
            border-width: 2px;
          }
          
          .dropdown-item:hover,
          .dropdown-item:focus {
            background-color: var(--bs-dark);
            color: var(--bs-white);
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .dropdown-menu {
            animation: none;
          }
          
          .btn,
          .dropdown-item {
            transition: none;
          }
        }
      `}</style>
    </div>
  );
};

ExportButton.displayName = 'ExportButton';

export default ExportButton;